/**
 * GitLab Pipeline Layout 组件
 * 使用 PatternFly Topology 渲染 Pipeline 流程图
 */

import { useState, useCallback, useEffect } from 'react';
import {
  Controller,
  GraphModel,
  EdgeModel,
  NodeModel,
  Node,
  Rect,
  Visualization,
  VisualizationProvider,
  VisualizationSurface,
  GRAPH_LAYOUT_END_EVENT,
  GRAPH_POSITION_CHANGE_EVENT,
} from '@patternfly/react-topology';
import {
  DROP_SHADOW_SPACING,
  GRAPH_MIN_WIDTH,
  NODE_HEIGHT,
  PipelineLayout as PipelineLayoutType,
} from './gitlab-topology-const';
import { PipelineMixedNodeModel } from './gitlab-topology-utils';
import gitlabComponentFactory, { layoutFactory } from './gitlabComponentFactory';

type GitLabPipelineLayoutProps = {
  model: {
    graph: GraphModel;
    nodes: PipelineMixedNodeModel[];
    edges: EdgeModel[];
  };
};

export const GitLabPipelineLayout = ({ model }: GitLabPipelineLayoutProps) => {
  const [vis, setVis] = useState<Controller | null>(null);
  const [maxSize, setMaxSize] = useState<{
    height: number;
    width: number;
  }>({ height: 0, width: 0 });

  const layout: PipelineLayoutType = model.graph.layout as PipelineLayoutType;

  const onLayoutUpdate = useCallback(
    (nodes: Node[]) => {
      const nodeBounds = nodes.map((node: Node<NodeModel, any>) =>
        node.getBounds(),
      );
      const maxWidth = Math.floor(
        nodeBounds
          .map((bounds: Rect) => bounds.width)
          .reduce((w1: number, w2: number) => Math.max(w1, w2), 0),
      );
      const maxHeight = Math.floor(
        nodeBounds
          .map((bounds: Rect) => bounds.height)
          .reduce((h1: number, h2: number) => Math.max(h1, h2), 0),
      );

      const maxX = Math.floor(
        nodeBounds
          .map((bounds: Rect) => bounds.x)
          .reduce((x1: number, x2: number) => Math.max(x1, x2), 0),
      );
      const maxY = Math.floor(
        nodeBounds
          .map((bounds: Rect) => bounds.y)
          .reduce((y1: number, y2: number) => Math.max(y1, y2), 0),
      );

      const horizontalMargin = DROP_SHADOW_SPACING * 2;
      const verticalMargin = DROP_SHADOW_SPACING * 2;

      const height = maxHeight + maxY + verticalMargin;
      const width = Math.max(maxWidth + maxX + horizontalMargin, GRAPH_MIN_WIDTH);

      setMaxSize({ height, width });
    },
    [layout],
  );

  useEffect(() => {
    if (!vis) {
      const visualization = new Visualization();
      visualization.registerLayoutFactory(layoutFactory);
      visualization.registerComponentFactory(gitlabComponentFactory);
      
      // 初始化 graph model
      visualization.fromModel(
        {
          graph: model.graph,
          nodes: model.nodes,
          edges: model.edges,
        },
        false,
      );
      
      setVis(visualization);
    }
  }, [vis, model]);

  useEffect(() => {
    if (!vis) return;
    
    try {
      const controller = vis.getGraph();
      if (!controller) return;
      
      controller.addEventListener(GRAPH_LAYOUT_END_EVENT, onLayoutUpdate);
      controller.addEventListener(GRAPH_POSITION_CHANGE_EVENT, onLayoutUpdate);

      // 触发布局
      controller.layout();

      return () => {
        controller.removeEventListener(GRAPH_LAYOUT_END_EVENT, onLayoutUpdate);
        controller.removeEventListener(
          GRAPH_POSITION_CHANGE_EVENT,
          onLayoutUpdate,
        );
      };
    } catch (error) {
      // Graph 未准备好
    }
  }, [vis, onLayoutUpdate]);

  if (!vis) {
    return null;
  }

  return (
    <VisualizationProvider controller={vis}>
      <VisualizationSurface
        state={{
          selectedIds: [],
        }}
        style={{
          height: maxSize.height || NODE_HEIGHT,
          width: maxSize.width || GRAPH_MIN_WIDTH,
        }}
      />
    </VisualizationProvider>
  );
};

